// Diferencias entre dos snapshots de versión de orden.
// Los snapshots llegan como JSON string con las claves del modelo PHP
// (snake_case), así que aquí se leen tal cual.

const GENERAL_FIELDS = [
  ["status", "Status"],
  ["purchase_order_number", "PO number"],
  ["customer_email", "Customer email"],
  ["salesman", "Salesman"],
  ["shipping_preference", "Shipping preference"],
  ["payment_type", "Payment type"],
  ["vin", "VIN"],
  ["customer_notes", "Customer notes"]
];

const MONEY_FIELDS = [
  ["products_total", "Products total"],
  ["discount", "Discount"],
  ["shipping_paid", "Shipping"],
  ["core", "Core"],
  ["tax", "Tax"],
  ["payment_fees", "Payment fees"],
  ["total", "Total"]
];

const ADDRESS_BLOCKS = [
  ["billing_address", "Billing address"],
  ["shipping_address", "Shipping address"],
  ["forwarder_address", "Forwarder address"]
];

const ADDRESS_FIELDS = [
  "name",
  "company",
  "street1",
  "street2",
  "street3",
  "city",
  "state",
  "postal_code",
  "country",
  "phone"
];

const ITEM_FIELDS = [
  ["description", "Description"],
  ["quantity", "Qty", "number"],
  ["price", "Price", "money"],
  ["brand", "Brand"],
  ["line", "Line"],
  ["warehouse_code", "Warehouse"]
];

function normalize(value, type) {
  if (value === null || value === undefined || value === "") return "";
  if (type === "money" || type === "number") {
    const number = Number(value);
    return Number.isNaN(number) ? String(value) : number;
  }
  return String(value).trim();
}

function display(value, type) {
  if (value === "") return "—";
  if (type === "money") return `$${Number(value).toFixed(2)}`;
  return String(value);
}

function compareField(before, after, key, label, type) {
  const from = normalize(before && before[key], type);
  const to = normalize(after && after[key], type);
  if (from === to) return null;
  return { label, from: display(from, type), to: display(to, type) };
}

function compareFields(before, after, fields, type) {
  return fields
    .map(([key, label, fieldType]) =>
      compareField(before, after, key, label, fieldType || type)
    )
    .filter(Boolean);
}

function addressText(address) {
  if (!address) return "";
  return ADDRESS_FIELDS.map(field => normalize(address[field]))
    .filter(Boolean)
    .join(", ");
}

export function parseSnapshot(snapshotJson) {
  try {
    return JSON.parse(snapshotJson);
  } catch (e) {
    return null;
  }
}

/**
 * Devuelve { general, totals, addresses, items, hasChanges }.
 * `items` es una lista de { status: added|removed|changed, nkrCode, description, changes }.
 * Los productos se emparejan por el id de orders_v2_products.
 */
export function diffOrderSnapshots(before, after) {
  const general = compareFields(before, after, GENERAL_FIELDS);
  const totals = compareFields(before, after, MONEY_FIELDS, "money");

  const addresses = ADDRESS_BLOCKS.map(([key, label]) => {
    const from = addressText(before[key]);
    const to = addressText(after[key]);
    if (from === to) return null;
    return { label, from: from || "—", to: to || "—" };
  }).filter(Boolean);

  const beforeItems = new Map(
    (before.items || []).map(item => [item.id, item])
  );
  const afterItems = new Map((after.items || []).map(item => [item.id, item]));
  const items = [];

  afterItems.forEach((item, id) => {
    const previous = beforeItems.get(id);
    if (!previous) {
      items.push({
        status: "added",
        nkrCode: item.nkr_code,
        description: item.description,
        changes: [
          {
            label: "Qty × price",
            from: "—",
            to: `${item.quantity} × ${display(
              normalize(item.price, "money"),
              "money"
            )}`
          }
        ]
      });
      return;
    }
    const changes = compareFields(previous, item, ITEM_FIELDS);
    if (changes.length) {
      items.push({
        status: "changed",
        nkrCode: item.nkr_code,
        description: item.description,
        changes
      });
    }
  });

  beforeItems.forEach((item, id) => {
    if (!afterItems.has(id)) {
      items.push({
        status: "removed",
        nkrCode: item.nkr_code,
        description: item.description,
        changes: [
          {
            label: "Qty × price",
            from: `${item.quantity} × ${display(
              normalize(item.price, "money"),
              "money"
            )}`,
            to: "—"
          }
        ]
      });
    }
  });

  return {
    general,
    totals,
    addresses,
    items,
    hasChanges:
      general.length + totals.length + addresses.length + items.length > 0
  };
}
